"use client";

import React from "react";
import Link from "next/link";
import { Terminal, RefreshCw } from "lucide-react";

export default function NotFound() {
  const [progress, setProgress] = React.useState(0);
  const [sadFace, setSadFace] = React.useState(":(");

  React.useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.floor(Math.random() * 15) + 5;
      });
    }, 400);

    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const faceInterval = setInterval(() => {
      setSadFace(";(");
      setTimeout(() => {
        setSadFace(":(");
      }, 150);
    }, 3500);

    return () => clearInterval(faceInterval);
  }, []);

  return (
    <div className="fixed inset-0 bg-[#0078d7] text-white font-mono flex flex-col justify-between p-8 md:p-16 select-none z-50">
      <div className="max-w-3xl space-y-8 my-auto">
        {/* Sad Face Emoticon with Blinking Animation */}
        <div className="text-7xl md:text-8xl font-light select-none tracking-tighter">
          {sadFace}
        </div>

        {/* Main BSOD Message */}
        <h1 className="text-lg md:text-xl leading-relaxed font-light">
          Ocorreu um problema e a página que você está procurando não pôde ser encontrada no Bold Workspace. 
          Estamos apenas coletando algumas informações sobre o erro (404 Not Found) e você poderá retornar ao sistema.
        </h1>

        {/* Progress bar and redirection percentage */}
        <div className="text-base md:text-lg font-light flex items-center gap-3">
          <span>{Math.min(progress, 100)}% concluído</span>
          {progress < 100 && (
            <RefreshCw className="size-4 animate-spin text-blue-200" />
          )}
        </div>

        {/* Error info section */}
        <div className="flex flex-col md:flex-row gap-6 items-start pt-4 border-t border-blue-400/40">
          {/* Mock QR Code representation */}
          <div className="bg-white p-2 rounded-lg shrink-0 hidden sm:block">
            <svg className="size-20 text-black" viewBox="0 0 100 100" fill="currentColor">
              {/* Top-Left anchor */}
              <rect x="10" y="10" width="30" height="30" />
              <rect x="20" y="20" width="10" height="10" fill="white" />
              {/* Top-Right anchor */}
              <rect x="60" y="10" width="30" height="30" />
              <rect x="70" y="20" width="10" height="10" fill="white" />
              {/* Bottom-Left anchor */}
              <rect x="10" y="60" width="30" height="30" />
              <rect x="20" y="70" width="10" height="10" fill="white" />
              {/* Random QR code pixels */}
              <rect x="45" y="15" width="8" height="8" />
              <rect x="50" y="30" width="8" height="8" />
              <rect x="45" y="45" width="8" height="8" />
              <rect x="75" y="50" width="8" height="8" />
              <rect x="60" y="65" width="8" height="8" />
              <rect x="70" y="75" width="8" height="8" />
              <rect x="80" y="60" width="8" height="8" />
            </svg>
          </div>

          <div className="space-y-3 text-xs md:text-sm text-blue-100 font-sans">
            <p>
              Para obter mais informações sobre esse problema e possíveis soluções, acesse:
              <br />
              <a 
                href="https://boldworkspace.com.br/suporte" 
                target="_blank" 
                rel="noreferrer" 
                className="underline hover:text-white"
              >
                https://boldworkspace.com.br/suporte
              </a>
            </p>
            
            <p className="pt-2">
              Se você entrar em contato com um administrador do sistema, forneça estas informações:
              <br />
              <span className="font-mono text-white font-bold block mt-1">Código de parada: 404_PAGE_NOT_FOUND</span>
              <span className="font-mono text-white font-bold block">O que falhou: URL_REQUEST_PATH</span>
            </p>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="flex flex-wrap gap-4 items-center justify-between border-t border-blue-400/40 pt-4 text-xs font-sans text-blue-200">
        <div className="flex items-center gap-2">
          <Terminal className="size-4" />
          <span>Bold OS kernel version 3.5.0-release</span>
        </div>
        <Link 
          href="/" 
          className="bg-white text-[#0078d7] hover:bg-blue-50 transition-colors px-4 py-2 rounded font-bold text-xs cursor-pointer shadow-md focus:outline-none"
        >
          Voltar para o Início
        </Link>
      </div>
    </div>
  );
}
